<template>
  <v-row class="DeviceEditor custom-scroll-bar" justify="center">
    <v-dialog
      v-model="deletePluviometricChartDialog"
      width="500"
      content-class="delete-pluviometric-chart-dialog"
    >
      <v-card>
        <v-card-title class="title-text">
          {{ $t('AddDevice.deletePluviometricChartTitle') }}
        </v-card-title>
        <v-card-actions class="card-actions">
          <v-btn class="secondary-btn" @click="cancelDeletePluviometricChart">
            {{ $t('AddDevice.cancel') }}
          </v-btn>
          <v-btn class="main-btn" @click="deletePluviometricChart">
            {{ $t('AddDevice.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-col cols="12" sm="3" style="text-align: -webkit-center">
      <v-img max-height="124" max-width="124" :src="require('@/assets/min-irrigation.png')"/>
      <v-row no-gutters class="mt-4">
        <v-col cols="12">
          <v-text-field
            :label="`${$t('AddDevice.equipmentName')} *`"
            :title="`${$t('AddDevice.equipmentName')} *`"
            v-model="deviceData.name"
            outlined
            background-color="#FFF"
            color="#0098E1"
            :rules="rules.fieldRequired"
            required
            validate-on-blur
            maxlength="30"
            :disabled="fieldsLockedByWebhook"
          />
        </v-col>
      </v-row>
      <template v-if="!loadingCharts">
        <p class="pluviometric-configuration px-2">{{ $t('AddDevice.pluviometricConfiguration') }}</p>
        <v-list v-if="pluviometricChartsData.length" class="pluviometric-charts-list">
          <v-list-item-group
            v-model="selectedPluviometricConfiguration"
            color="#0098E1"
          >
            <template v-for="(pluviometricChart, index) in deviceData.pluviometricCharts">
              <v-divider v-if="index > 0" />
              <v-list-item>
                <v-list-item-action class="mr-0">
                  <v-icon v-if="pluviometricChart.isActive" small color="#00D166">circle</v-icon>
                </v-list-item-action>

                <v-list-item-title v-if="pluviometricChart.validityDate && pluviometricChartsData[index].pumpFlow.value" class="text-left">
                  {{ parseValidityDate(pluviometricChart.validityDate) }} - {{  pluviometricChartsData[index].pumpFlow.value  }} m3/h
                </v-list-item-title>
                <v-list-item-title v-else-if="pluviometricChart.validityDate && !pluviometricChartsData[index].pumpFlow.value" class="text-left">
                  {{ parseValidityDate(pluviometricChart.validityDate) }}
                </v-list-item-title>
                <v-list-item-title v-else-if="!pluviometricChart.validityDate && pluviometricChartsData[index].pumpFlow.value" class="text-left">
                  {{  pluviometricChartsData[index].pumpFlow.value  }} m3/h
                </v-list-item-title>
                <v-list-item-title v-else class="text-left">{{ $t('AddDevice.newPluviometricChart') }}</v-list-item-title>
                <v-list-item-action v-if="deviceData.pluviometricCharts.length > 1">
                  <v-btn small icon @click.stop="handleDeletePluviometricChart(index)"><v-icon small color="#F93A2F">mdi-trash-can-outline</v-icon></v-btn>
                </v-list-item-action>
              </v-list-item>
            </template>

          </v-list-item-group>
        </v-list>
        <v-row class="mt-4 ml-2" justify="start">
          <a @click="addPluviometricChart" class="add-pluviometric-chart"><v-icon small color="#0099E1">mdi-file-plus</v-icon> {{ $t('PluviometricChart.addPluviometricChart') }}</a>
        </v-row>
        <v-row class="mt-4 ml-2" justify="start">
          <p><v-icon small color="#00D166">circle</v-icon> {{$t('PluviometricChart.activeConfiguration')}}</p>
        </v-row>
      </template>
    </v-col>
    <v-col cols="12" sm="9" class="text-left">
      <div v-if="loadingCharts" class="display-1 text-center full-height align-content-center">
        <v-progress-circular
          indeterminate
          color="#0098E1"
        />
      </div>
      <v-form class="mt-7" ref="deviceForm" lazy-validation v-else-if="selectedPluviometricConfiguration !== undefined && selectedPluviometricConfiguration !== null && pluviometricChartsData[selectedPluviometricConfiguration]">
        <v-row v-if="deviceData.type === 2" no-gutters>
          <v-col cols="12" sm="5" class="px-2">
            <v-select
              :items="equipmentSubtypes.linear"
              item-value="value"
              item-text="label"
              v-model="deviceData.subtype"
              :label="`${$t('AddDevice.equipmentSubtype')} *`"
              :title="`${$t('AddDevice.equipmentSubtype')} *`"
              outlined
              background-color="#FFF"
              color="#0098E1"
              :rules="rules.fieldRequired"
              required
              validate-on-blur
              :disabled="fieldsLockedByWebhook"
            />
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12" sm="4" class="px-2">
            <v-menu
              v-model="validityDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="deviceData.pluviometricCharts[selectedPluviometricConfiguration].formattedValidityDate"
                  :label="`${$t('AddDevice.validityDate')} *`"
                  :title="`${$t('AddDevice.validityDate')} *`"
                  ref="validityDateTextField"
                  append-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.fieldRequired"
                  required
                  clearable
                  :disabled="fieldsLockedByWebhook"
                  @click:clear="clearValidityDate"
                />
              </template>
              <v-date-picker
                v-model="deviceData.pluviometricCharts[selectedPluviometricConfiguration].auxValidityDate"
                color="#0098E1"
                locale="es-AR"
                @input="updateValidityDate"
              />
            </v-menu>
          </v-col>
        </v-row>
        <v-row no-gutters class="pluviometric-file-row">
          <v-col cols="12" sm="4" class="px-2">
            <v-file-input
              class="FileSelector"
              v-model="deviceData.pluviometricCharts[selectedPluviometricConfiguration].pluviometricChartFile"
              ref="FileSelector"
              :label="$t('AddDevice.pluviometricChart')"
              :title="$t('AddDevice.pluviometricChart')"
              outlined
              background-color="#FFF"
              color="#0098E1"
              :rules="rules.fileField"
              prepend-icon=""
              :clearable="false"
              :accept="allowedFileTypes"
            >
              <template v-slot:append>
                <v-btn
                  class="SearchFileBtn"
                  elevation="0"
                  color="#0098E1"
                  dark
                  @click="triggerFileSelector"
                >{{$t('AddDevice.search')}}</v-btn>
              </template>
            </v-file-input>
          </v-col>
          <v-col cols="12" sm="4" class="px-2 automatic-reading-col">
            <div class="automatic-reading-col-inner">
              <div class="automatic-reading-block">
              <v-progress-circular
                v-if="fieldsLockedByWebhook"
                indeterminate
                color="#FFAD46"
                size="24"
                width="2"
                class="mr-2"
              />
              <v-icon v-if="!fieldsLockedByWebhook" small class="mr-1" color="#FFAD46">{{ hasAutomaticReadingForCurrentChart ? 'mdi-check-circle' : 'mdi-creation' }}</v-icon>
              <span class="text-body2 automatic-reading-label">{{ $t('PluviometricChart.automaticReading') }}</span>
              </div>
            </div>
          </v-col>
          <v-col cols="12" sm="4" class="px-2">
            <v-menu v-model="creationDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field                 v-model="deviceData.pluviometricCharts[selectedPluviometricConfiguration].formattedCreationDate" :label="$t('PluviometricChart.creationDate')" :title="$t('PluviometricChart.creationDate')"
                  append-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" outlined background-color="#FFF"
                  color="#0098E1" clearable :disabled="fieldsLockedByWebhook || !deviceData.pluviometricCharts[selectedPluviometricConfiguration].pluviometricChartFile" @click:clear="clearCreationDate"/>
              </template>
              <v-date-picker v-model="deviceData.pluviometricCharts[selectedPluviometricConfiguration].auxCreationDate" color="#0098E1" locale="es-AR" @input="updateCreationDate">
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12" sm="4" class="px-2">
            <v-row no-gutters>
              <v-col cols="8">
                <v-text-field
                  :label="`${$t('PluviometricChart.pumpFlow')} *`"
                  :title="`${$t('PluviometricChart.pumpFlow')} *`"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].pumpFlow.value"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberRequired"
                  required
                  validate-on-blur
                  :disabled="fieldsLockedByWebhook"
                  @input="setFieldSourceManual('pumpFlow')"
                >
                  <template v-slot:append>
                    <v-icon v-if="isFieldFromWebhook('pumpFlow')" small color="#FFAD46">mdi-creation</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="4" class="pl-1">
                <v-radio-group
                  v-model="pluviometricChartsData[selectedPluviometricConfiguration].pumpFlow.unit"
                  row
                  :disabled="fieldsLockedByWebhook"
                >
                  <v-radio
                    :label="$t('PluviometricChart.units.cubicMeter') + '/' + $t('PluviometricChart.units.hour')"
                    :title="$t('PluviometricChart.units.cubicMeter') + '/' + $t('PluviometricChart.units.hour')"
                    value="m3/h"
                  />
                  <!-- <v-radio
                    :label="$t('PluviometricChart.units.liter') + '/' + $t('PluviometricChart.units.second')"
                    :title="$t('PluviometricChart.units.liter') + '/' + $t('PluviometricChart.units.second')"
                    value="l/s"
                  />
                  <v-radio
                    :label="$t('PluviometricChart.units.gallonsPerMinute')"
                    :title="$t('PluviometricChart.units.gallonsPerMinute')"
                    value="gpm"
                  /> -->
                </v-radio-group>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" sm="4" class="px-2">
            <v-select :menu-props="{ bottom: true, offsetY: true }" :items="brands" item-value="value" item-text="name" v-model="pluviometricChartsData[selectedPluviometricConfiguration].brand" :label="`${$t('PluviometricChart.brand')} *`" :title="`${$t('PluviometricChart.brand')} *`" outlined
              background-color="#FFF" color="#0098E1" :rules="rules.fieldRequired" required validate-on-blur :disabled="fieldsLockedByWebhook" @change="onBrandChange">
              <template v-slot:append>
                <v-icon v-if="isFieldFromWebhook('brand')" small color="#FFAD46">mdi-creation</v-icon>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" sm="4" class="px-2">
            <v-select :disabled="!currentBrand || fieldsLockedByWebhook" :menu-props="{ bottom: true, offsetY: true }" :items="models" item-text="name" item-value="value" v-model="pluviometricChartsData[selectedPluviometricConfiguration].model" :label="$t('PluviometricChart.model')" :title="$t('PluviometricChart.model')" outlined
              background-color="#FFF" color="#0098E1" @change="setFieldSourceManual('model')">
              <template v-slot:append>
                <v-icon v-if="isFieldFromWebhook('model')" small color="#FFAD46">mdi-creation</v-icon>
              </template>
            </v-select>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12" sm="4" class="px-2">
            <v-row no-gutters v-if="!isIREquipment">
              <v-col cols="8">
                <v-text-field
                  :label="`${$t('PluviometricChart.length')} *`"
                  :title="`${$t('PluviometricChart.length')} *`"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].deviceLength.value"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberRequired"
                  required
                  validate-on-blur
                  :disabled="fieldsLockedByWebhook"
                  @blur="updateDeviceLength"
                  @input="setFieldSourceManual('deviceLength')"
                >
                  <template v-slot:append>
                    <v-icon v-if="isFieldFromWebhook('deviceLength')" small color="#FFAD46">mdi-creation</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="4" class="pl-1">
                <v-radio-group
                  v-model="pluviometricChartsData[selectedPluviometricConfiguration].deviceLength.unit"
                  row
                  :disabled="fieldsLockedByWebhook"
                >
                  <v-radio
                    :label="$t('PluviometricChart.units.meter')"
                    :title="$t('PluviometricChart.units.meter')"
                    value="m"
                  />
                  <!-- <v-radio
                    :label="$t('PluviometricChart.units.foot')"
                    :title="$t('PluviometricChart.units.foot')"
                    value="ft"
                  /> -->
                </v-radio-group>
              </v-col>
            </v-row>
            <v-row no-gutters v-else>
              <v-col cols="6">
                <v-text-field
                  :label="`${$t('PluviometricChart.hoseLength')} *`"
                  :title="`${$t('PluviometricChart.hoseLength')} *`"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].hoseLength.value"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberRequired"
                  required
                  validate-on-blur
                  :disabled="fieldsLockedByWebhook"
                  @input="setFieldSourceManual('hoseLength')"
                >
                  <template v-slot:append>
                    <v-icon v-if="isFieldFromWebhook('hoseLength')" small color="#FFAD46">mdi-creation</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="6" class="pl-1">
                <v-radio-group
                  v-model="pluviometricChartsData[selectedPluviometricConfiguration].hoseLength.unit"
                  :disabled="fieldsLockedByWebhook"
                  row
                >
                  <v-radio
                    :label="$t('PluviometricChart.units.meter')"
                    :title="$t('PluviometricChart.units.meter')"
                    value="m"
                  />
                </v-radio-group>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" sm="5" class="px-2">
            <v-row no-gutters v-if="!isIREquipment">
              <v-col cols="8">
                <v-text-field
                  :label="`${$t('PluviometricChart.area')} *`"
                  :title="`${$t('PluviometricChart.area')} *`"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].area.value"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberRequired"
                  required
                  validate-on-blur
                  :disabled="fieldsLockedByWebhook"
                  @input="setFieldSourceManual('area')"
                >
                  <template v-slot:append>
                    <v-icon v-if="isFieldFromWebhook('area')" small color="#FFAD46">mdi-creation</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="4" class="pl-1">
                <v-radio-group
                  v-model="pluviometricChartsData[selectedPluviometricConfiguration].area.unit"
                  row
                  :disabled="fieldsLockedByWebhook"
                >
                  <v-radio
                    :label="$t('PluviometricChart.units.hectare')"
                    :title="$t('PluviometricChart.units.hectare')"
                    value="ha"
                  />
                  <!-- <v-radio
                    :label="$t('PluviometricChart.units.acres')"
                    :title="$t('PluviometricChart.units.acres')"
                    value="acres"
                  /> -->
                </v-radio-group>
              </v-col>
            </v-row>
            <v-row no-gutters v-else>
              <v-col cols="8">
                <v-text-field
                  :label="`${$t('PluviometricChart.cannonReach')} *`"
                  :title="`${$t('PluviometricChart.cannonReach')} *`"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].equipmentReach.value"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberRequired"
                  required
                  validate-on-blur
                  :disabled="fieldsLockedByWebhook"
                  @input="setFieldSourceManual('equipmentReach')"
                >
                  <template v-slot:append>
                    <v-icon v-if="isFieldFromWebhook('equipmentReach')" small color="#FFAD46">mdi-creation</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="4" class="pl-1">
                <v-radio-group
                  v-model="pluviometricChartsData[selectedPluviometricConfiguration].equipmentReach.unit"
                  :disabled="fieldsLockedByWebhook"
                  row
                >
                <v-radio
                    :label="$t('PluviometricChart.units.meter')"
                    :title="$t('PluviometricChart.units.meter')"
                    value="m"
                  />
                </v-radio-group>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <p class="category-text text-left px-2">{{ $t('PluviometricChart.pressureRange') }}</p>
        <v-row no-gutters>
          <v-col cols="12" sm="4" class="px-2">
            <v-row no-gutters>
              <v-col cols="8">
                <v-text-field
                  :label="`${$t('PluviometricChart.chartPressure')} *`"
                  :title="`${$t('PluviometricChart.chartPressure')} *`"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].pressureRange.chartPressure"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberRequired"
                  required
                  validate-on-blur
                  :disabled="fieldsLockedByWebhook"
                  @input="onChartPressureInput"
                >
                  <template v-slot:append>
                    <v-icon v-if="isFieldFromWebhook('chartPressure')" small color="#FFAD46">mdi-creation</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="4" class="pl-1">
                <v-radio-group
                  v-model="pluviometricChartsData[selectedPluviometricConfiguration].pressureRange.unit"
                  row
                  :disabled="fieldsLockedByWebhook"
                >
                  <v-radio
                    :label="$t('PluviometricChart.units.bar')"
                    :title="$t('PluviometricChart.units.bar')"
                    value="bar"
                  />
                  <!-- <v-radio
                    :label="$t('PluviometricChart.units.psi')"
                    :title="$t('PluviometricChart.units.psi')"
                    value="psi"
                  /> -->
                </v-radio-group>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="6" sm="4" class="px-2">
            <v-text-field
              ref="minPressureField"
              :label="`${$t('PluviometricChart.minPressure')} *`"
              :title="`${$t('PluviometricChart.minPressure')} *`"
              v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].pressureRange.minPressure"
              outlined
              background-color="#FFF"
              color="#0098E1"
              :rules="rules.minPressure"
              required
              validate-on-blur
              :disabled="fieldsLockedByWebhook"
              @input="setFieldSourceManual('minPressure')"
            >
              <template v-slot:append>
                <v-icon v-if="isFieldFromWebhook('minPressure')" small color="#FFAD46">mdi-creation</v-icon>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="6" sm="4" class="px-2">
            <v-text-field
              ref="maxPressureField"
              :label="`${$t('PluviometricChart.maxPressure')} *`"
              :title="`${$t('PluviometricChart.maxPressure')} *`"
              v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].pressureRange.maxPressure"
              outlined
              background-color="#FFF"
              color="#0098E1"
              :rules="rules.maxPressure"
              required
              validate-on-blur
              :disabled="fieldsLockedByWebhook"
              @input="setFieldSourceManual('maxPressure')"
            >
              <template v-slot:append>
                <v-icon v-if="isFieldFromWebhook('maxPressure')" small color="#FFAD46">mdi-creation</v-icon>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row class="full-width" v-if="deviceData.equipment.type !== EQUIPMENT_TYPE.IR">
          <a @click="advancedConfigFlag = !advancedConfigFlag" class="advanced-cfg float-left px-2">{{ advancedConfigFlag ? '-' : '+' }} {{ $t('PluviometricChart.advancedConfig') }}</a>
        </v-row>
      </v-form>
      <div v-else class="text-center full-height align-content-center no-pluviometric-chart-selected">
        <p>{{$t('PluviometricChart.noPluviometricChartSelected')}}</p>
      </div>
    </v-col>
    <v-col cols="12" class="text-left" v-if="deviceData.equipment.type !== EQUIPMENT_TYPE.IR && !loadingCharts && selectedPluviometricConfiguration !== undefined">
      <v-form class="mt-7" ref="advancedDeviceForm" lazy-validation v-if="advancedConfigFlag">
        <v-row class="full-width pt-5 PluviometricChart">
          <p class="section-text text-left full-width">{{ $t('PluviometricChart.fieldArea')}}<v-icon @click="showFieldAreaSection = !showFieldAreaSection" class="ml-1">{{showFieldAreaSection ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon></p>
          <template v-if="showFieldAreaSection">
            <v-row class="full-width" no-gutters>
              <v-col cols="6">
                <v-row no-gutters>
                  <v-col cols="6">
                    <v-text-field
                      :label="$t('PluviometricChart.fullLapArea')"
                      :title="$t('PluviometricChart.fullLapArea')"
                      v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.fieldArea.fullLapArea"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      :rules="rules.numberOptional"
                    />
                  </v-col>
                  <v-col cols="6" class="pl-1">
                    <v-radio-group
                      v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.fieldArea.unit"
                      row
                    >
                      <v-radio
                        :label="$t('PluviometricChart.units.hectare')"
                        :title="$t('PluviometricChart.units.hectare')"
                        value="ha"
                      />
                      <v-radio
                        :label="$t('PluviometricChart.units.acres')"
                        :title="$t('PluviometricChart.units.acres')"
                        value="acres"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row class="full-width" no-gutters>
              <v-col cols="6">
                <v-text-field
                  :label="$t('PluviometricChart.areaWithCannon')"
                  :title="$t('PluviometricChart.areaWithCannon')"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.fieldArea.areaWithCannon"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberOptional"
                />
              </v-col>
            </v-row>
            <v-row class="full-width" no-gutters>
              <v-col cols="6">
                <v-row no-gutters>
                  <v-col cols="6">
                    <v-text-field
                      :label="$t('PluviometricChart.cannonRadius')"
                      :title="$t('PluviometricChart.cannonRadius')"
                      v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.cannonRadius.value"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      :rules="rules.numberOptional"
                    />
                  </v-col>
                  <v-col cols="6" class="pl-1">
                    <v-radio-group
                      v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.cannonRadius.unit"
                      row
                    >
                      <v-radio
                        :label="$t('PluviometricChart.units.meter')"
                        :title="$t('PluviometricChart.units.meter')"
                        value="m"
                      />
                      <v-radio
                        :label="$t('PluviometricChart.units.foot')"
                        :title="$t('PluviometricChart.units.foot')"
                        value="ft"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </template>
          <p class="section-text text-left full-width">{{ $t('PluviometricChart.flow')}}<v-icon @click="showFlowSection = !showFlowSection" class="ml-1">{{showFlowSection ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon></p>
          <template v-if="showFlowSection">
            <v-row class="full-width" no-gutters>
              <v-col cols="9">
                <v-row no-gutters>
                  <v-col cols="4">
                    <v-text-field
                      :label="$t('PluviometricChart.flowPerArea')"
                      :title="$t('PluviometricChart.flowPerArea')"
                      v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.flowPerArea.value"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      :rules="rules.numberOptional"
                    />
                  </v-col>
                  <v-col cols="8" class="pl-1">
                    <v-radio-group
                      v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.flowPerArea.unit"
                      row
                    >
                      <v-radio
                        :label="$t('PluviometricChart.units.litersPerSecond') + ' ' + $t('PluviometricChart.units.per') + ' ' + $t('PluviometricChart.units.hectare')"
                        :title="$t('PluviometricChart.units.litersPerSecond') + ' ' + $t('PluviometricChart.units.per') + ' ' + $t('PluviometricChart.units.hectare')"
                        value="lps per ha"
                      />
                      <v-radio
                        :label="$t('PluviometricChart.units.gallonsPerMinute') + ' ' + $t('PluviometricChart.units.per') + ' ' + $t('PluviometricChart.units.acre')"
                        :title="$t('PluviometricChart.units.gallonsPerMinute') + ' ' + $t('PluviometricChart.units.per') + ' ' + $t('PluviometricChart.units.acre')"
                        value="gpm per acre"
                      />
                      <v-radio
                        :label="$t('PluviometricChart.units.cubicMeter') + '/' + $t('PluviometricChart.units.hour') + ' ' + $t('PluviometricChart.units.per') + ' ' + $t('PluviometricChart.units.hectare')"
                        :title="$t('PluviometricChart.units.cubicMeter') + '/' + $t('PluviometricChart.units.hour') + ' ' + $t('PluviometricChart.units.per') + ' ' + $t('PluviometricChart.units.hectare')"
                        value="m3/h per ha"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row class="full-width" no-gutters>
              <v-col cols="9">
                <v-row no-gutters>
                  <v-col cols="4">
                    <v-text-field
                      :label="$t('PluviometricChart.flowPerDay')"
                      :title="$t('PluviometricChart.flowPerDay')"
                      v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.flowPerDay.value"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      :rules="rules.numberOptional"
                    />
                  </v-col>
                  <v-col cols="8" class="pl-1">
                    <v-radio-group
                      v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.flowPerDay.unit"
                      row
                    >
                      <v-radio
                        :label="$t('PluviometricChart.units.milimeters') + '/' + $t('PluviometricChart.units.day')"
                        :title="$t('PluviometricChart.units.milimeters') + '/' + $t('PluviometricChart.units.day')"
                        value="mm/day"
                      />
                      <v-radio
                        :label="$t('PluviometricChart.units.inch') + '/' + $t('PluviometricChart.units.day')"
                        :title="$t('PluviometricChart.units.inch') + '/' + $t('PluviometricChart.units.day')"
                        value="in/day"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row class="full-width" no-gutters>
              <v-col cols="9">
                <v-row no-gutters>
                  <v-col cols="4">
                    <v-text-field
                      :label="$t('PluviometricChart.cannonFlow')"
                      :title="$t('PluviometricChart.cannonFlow')"
                      v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.cannonFlow.value"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      :rules="rules.numberOptional"
                    />
                  </v-col>
                  <v-col cols="8" class="pl-1">
                    <v-radio-group
                      v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.cannonFlow.unit"
                      row
                    >
                      <v-radio
                        :label="$t('PluviometricChart.units.litersPerSecond')"
                        :title="$t('PluviometricChart.units.litersPerSecond')"
                        value="lps"
                      />
                      <v-radio
                        :label="$t('PluviometricChart.units.gallonsPerMinute')"
                        :title="$t('PluviometricChart.units.gallonsPerMinute')"
                        value="gpm"
                      />
                      <v-radio
                        :label="$t('PluviometricChart.units.cubicMeter') + '/' + $t('PluviometricChart.units.hour')"
                        :title="$t('PluviometricChart.units.cubicMeter') + '/' + $t('PluviometricChart.units.hour')"
                        value="m3/h"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row class="full-width" no-gutters>
              <v-col cols="9">
                <v-row no-gutters>
                  <v-col cols="4">
                    <v-text-field
                      :label="$t('PluviometricChart.fullApplicationRate')"
                      :title="$t('PluviometricChart.fullApplicationRate')"
                      v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.fullApplicationRate.value"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      :rules="rules.numberOptional"
                    />
                  </v-col>
                  <v-col cols="8" class="pl-1">
                    <v-radio-group
                      v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.fullApplicationRate.unit"
                      row
                    >
                      <v-radio
                        :label="$t('PluviometricChart.units.milimeters')"
                        :title="$t('PluviometricChart.units.milimeters')"
                        value="mm"
                      />
                      <v-radio
                        :label="$t('PluviometricChart.units.inch')"
                        :title="$t('PluviometricChart.units.inch')"
                        value="inch"
                      />
                      <v-radio
                        :label="$t('PluviometricChart.units.cubicMeter') + '/' + $t('PluviometricChart.units.hour')"
                        :title="$t('PluviometricChart.units.cubicMeter') + '/' + $t('PluviometricChart.units.hour')"
                        value="m3/h"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </template>
          <p class="section-text text-left full-width">{{ $t('PluviometricChart.pressure')}}</p>
          <v-row class="full-width" no-gutters>
            <v-col cols="9">
              <v-row no-gutters>
                <v-col cols="4">
                  <v-text-field
                    :label="`${$t('PluviometricChart.basePress')} *`"
                    :title="`${$t('PluviometricChart.basePress')} *`"
                    v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pressure.basePress.value"
                    outlined
                    background-color="#FFF"
                    color="#0098E1"
                    :rules="rules.numberRequired"
                    required
                    validate-on-blur
                  />
                </v-col>
                <v-col cols="8" class="pl-1">
                  <v-radio-group
                    v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pressure.basePress.unit"
                    row
                  >
                    <v-radio
                      :label="$t('PluviometricChart.units.bar')"
                      :title="$t('PluviometricChart.units.bar')"
                      value="bar"
                    />
                    <!-- <v-radio
                      :label="$t('PluviometricChart.units.psi')"
                      :title="$t('PluviometricChart.units.psi')"
                      value="psi"
                    /> -->
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row class="full-width" no-gutters>
            <v-col cols="9">
              <v-row no-gutters>
                <v-col cols="4">
                  <v-text-field
                    :label="`${$t('PluviometricChart.pivotPress')} *`"
                    :title="`${$t('PluviometricChart.pivotPress')} *`"
                    v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pressure.pivotPress.value"
                    outlined
                    background-color="#FFF"
                    color="#0098E1"
                    :rules="rules.numberRequired"
                    required
                    validate-on-blur
                  />
                </v-col>
                <v-col cols="8" class="pl-1">
                  <v-radio-group
                    v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pressure.pivotPress.unit"
                    row
                  >
                    <v-radio
                      :label="$t('PluviometricChart.units.bar')"
                      :title="$t('PluviometricChart.units.bar')"
                      value="bar"
                    />
                    <!-- <v-radio
                      :label="$t('PluviometricChart.units.psi')"
                      :title="$t('PluviometricChart.units.psi')"
                      value="psi"
                    /> -->
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row class="full-width" no-gutters>
            <v-col cols="9">
              <v-row no-gutters>
                <v-col cols="4">
                  <v-text-field
                    :label="`${$t('PluviometricChart.endPress')} *`"
                    :title="`${$t('PluviometricChart.endPress')} *`"
                    v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pressure.endPress.value"
                    outlined
                    background-color="#FFF"
                    color="#0098E1"
                    :rules="rules.numberRequired"
                    required
                    validate-on-blur
                  />
                </v-col>
                <v-col cols="8" class="pl-1">
                  <v-radio-group
                    v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pressure.endPress.unit"
                    row
                  >
                    <v-radio
                      :label="$t('PluviometricChart.units.bar')"
                      :title="$t('PluviometricChart.units.bar')"
                      value="bar"
                    />
                    <!-- <v-radio
                      :label="$t('PluviometricChart.units.psi')"
                      :title="$t('PluviometricChart.units.psi')"
                      value="psi"
                    /> -->
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row class="full-width" no-gutters>
            <v-col cols="9">
              <v-row no-gutters>
                <v-col cols="4">
                  <v-text-field
                    :label="$t('PluviometricChart.highestElevation')"
                    :title="$t('PluviometricChart.highestElevation')"
                    v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pressure.highestElevation.value"
                    outlined
                    background-color="#FFF"
                    color="#0098E1"
                    :rules="rules.numberOptional"
                  />
                </v-col>
                <v-col cols="8" class="pl-1">
                  <v-radio-group
                    v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pressure.highestElevation.unit"
                    row
                  >
                    <v-radio
                      :label="$t('PluviometricChart.units.meter')"
                      :title="$t('PluviometricChart.units.meter')"
                      value="m"
                    />
                    <v-radio
                      :label="$t('PluviometricChart.units.foot')"
                      :title="$t('PluviometricChart.units.foot')"
                      value="ft"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row class="full-width" no-gutters>
            <v-col cols="9">
              <v-row no-gutters>
                <v-col cols="4">
                  <v-text-field
                    :label="$t('PluviometricChart.lowestElevation')"
                    :title="$t('PluviometricChart.lowestElevation')"
                    v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pressure.lowestElevation.value"
                    outlined
                    background-color="#FFF"
                    color="#0098E1"
                    :rules="rules.numberOptional"
                  />
                </v-col>
                <v-col cols="8" class="pl-1">
                  <v-radio-group
                    v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pressure.lowestElevation.unit"
                    row
                  >
                    <v-radio
                      :label="$t('PluviometricChart.units.meter')"
                      :title="$t('PluviometricChart.units.meter')"
                      value="m"
                    />
                    <v-radio
                      :label="$t('PluviometricChart.units.foot')"
                      :title="$t('PluviometricChart.units.foot')"
                      value="ft"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <p class="section-text text-left full-width">{{ $t('PluviometricChart.pipes')}}<v-icon @click="showPipesSection = !showPipesSection" class="ml-1">{{showPipesSection ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon></p>
          <template v-if="showPipesSection">
            <v-row class="full-width" no-gutters>
              <v-col cols="4">
                <v-text-field
                  :label="$t('PluviometricChart.tubesQty')"
                  :title="$t('PluviometricChart.tubesQty')"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pipes.tubeQuantity"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberOptional"
                />
              </v-col>
            </v-row>
            <v-row class="full-width" no-gutters>
              <v-col cols="9">
                <v-row no-gutters>
                  <v-col cols="4" style="align-self: center">
                    <p class="mb-0 category-text text-left full-width">{{ $t('PluviometricChart.tubeLength')}}</p>
                  </v-col>
                  <v-col cols="8" class="pl-1">
                    <v-radio-group
                      v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pipes.tubeLength"
                      row
                    >
                      <v-radio
                        :label="$t('PluviometricChart.units.meter')"
                        :title="$t('PluviometricChart.units.meter')"
                        value="m"
                      />
                      <v-radio
                        :label="$t('PluviometricChart.units.foot')"
                        :title="$t('PluviometricChart.units.foot')"
                        value="ft"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row class="full-width" no-gutters>
              <v-col cols="9">
                <v-row no-gutters>
                  <v-col cols="4" style="align-self: center">
                    <p class="mb-0 category-text text-left full-width">{{ $t('PluviometricChart.internalDiameter')}}</p>
                  </v-col>
                  <v-col cols="8" class="pl-1">
                    <v-radio-group
                      v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pipes.internalDiameterUnit"
                      row
                    >
                      <v-radio
                        :label="$t('PluviometricChart.units.milimeters')"
                        :title="$t('PluviometricChart.units.milimeters')"
                        value="mm"
                      />
                      <v-radio
                        :label="$t('PluviometricChart.units.inch')"
                        :title="$t('PluviometricChart.units.inch')"
                        value="in"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <template v-for="tube in pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.pipes.tubes">
              <v-row class="full-width" no-gutters>
                <v-col cols="2">
                  <v-text-field
                    :label="$t('PluviometricChart.tubes.length')"
                    :title="$t('PluviometricChart.tubes.length')"
                    v-model.number="tube.length"
                    outlined
                    background-color="#FFF"
                    color="#0098E1"
                    :rules="rules.numberOptional"
                  />
                </v-col>
                <v-col cols="2" class="ml-3">
                  <v-text-field
                    :label="$t('PluviometricChart.tubes.internalDiameter')"
                    :title="$t('PluviometricChart.tubes.internalDiameter')"
                    v-model.number="tube.internalDiameter"
                    outlined
                    background-color="#FFF"
                    color="#0098E1"
                    :rules="rules.numberOptional"
                  />
                </v-col>
                <v-col cols="2" class="ml-3">
                  <v-select :menu-props="{ bottom: true, offsetY: true }" :items="tubeMaterialsItems" item-value="value" item-text="name" v-model="tube.material" :label="`${$t('PluviometricChart.tubes.material')} *`" :title="`${$t('PluviometricChart.tubes.material')} *`" outlined
                  background-color="#FFF" color="#0098E1" :rules="rules.fieldRequired" required validate-on-blur/>
                </v-col>
                <v-col cols="2" class="ml-3">
                  <v-text-field
                    :label="$t('PluviometricChart.tubes.cFactor')"
                    :title="$t('PluviometricChart.tubes.cFactor')"
                    v-model.number="tube.cFactor"
                    outlined
                    background-color="#FFF"
                    color="#0098E1"
                    :rules="rules.numberOptional"
                  />
                </v-col>
                <v-col cols="2" class="ml-3">
                  <v-text-field
                    :label="$t('PluviometricChart.tubes.loss')"
                    :title="$t('PluviometricChart.tubes.loss')"
                    v-model.number="tube.loss"
                    outlined
                    background-color="#FFF"
                    color="#0098E1"
                    :rules="rules.numberOptional"
                  />
                </v-col>
              </v-row>
            </template>
          </template>
          <p class="section-text text-left full-width">{{ $t('PluviometricChart.sprinklerSettings')}}</p>
          <v-row class="full-width" no-gutters>
            <v-col cols="3">
              <v-text-field
                :label="`${$t('PluviometricChart.sprinklers.spanQty')} *`"
                :title="`${$t('PluviometricChart.sprinklers.spanQty')} *`"
                v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.flowPerSpan.spanQuantity"
                outlined
                background-color="#FFF"
                color="#0098E1"
                :rules="rules.numberOptional"
                required
                validate-on-blur
              />
            </v-col>
          </v-row>
          <v-row class="full-width" no-gutters>
            <v-col cols="9">
              <v-row no-gutters>
                <v-col cols="4" style="align-self: center">
                  <p class="mb-0 category-text text-left full-width">{{ $t('PluviometricChart.sprinklers.distToCenter')}}</p>
                </v-col>
                <v-col cols="8" class="pl-1">
                  <v-radio-group
                    v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.sprinklerConfiguration.distanceToCenterUnit"
                    row
                    :rules="rules.fieldRequired"
                    required
                    validate-on-blur
                  >
                    <v-radio
                      :label="$t('PluviometricChart.units.meter')"
                      :title="$t('PluviometricChart.units.meter')"
                      value="m"
                    />
                    <v-radio
                      :label="$t('PluviometricChart.units.foot')"
                      :title="$t('PluviometricChart.units.foot')"
                      value="ft"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row class="full-width" no-gutters>
            <v-col cols="9">
              <v-row no-gutters>
                <v-col cols="4" style="align-self: center">
                  <p class="mb-0 category-text text-left full-width">{{ $t('PluviometricChart.sprinklers.dropHeight')}}</p>
                </v-col>
                <v-col cols="8" class="pl-1">
                  <v-radio-group
                    v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.sprinklerConfiguration.dropHeightUnit"
                    row
                    :rules="rules.fieldRequired"
                    required
                    validate-on-blur
                  >
                    <v-radio
                      :label="$t('PluviometricChart.units.meter')"
                      :title="$t('PluviometricChart.units.meter')"
                      value="m"
                    />
                    <v-radio
                      :label="$t('PluviometricChart.units.foot')"
                      :title="$t('PluviometricChart.units.foot')"
                      value="ft"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <template
            v-for="(span, spanIndex) in pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.sprinklerConfiguration.sprinklersPerSpan"
          >
            <v-row no-gutters class="full-width my-2">
              <p class="section-text text-left align-content-center mb-0">{{ $t('PluviometricChart.span')}} {{ spanIndex + 1}}</p>
              <v-col class="pl-4" cols="2">
                <v-text-field
                  :label="`${$t('PluviometricChart.spans.sprinklerQuantity')} *`"
                  :title="`${$t('PluviometricChart.spans.sprinklerQuantity')} *`"
                  v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.flowPerSpan.spans[spanIndex].sprinklerQuantity"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberRequired"
                  required
                  validate-on-blur
                  hide-details="auto"
                />
              </v-col>
              <v-col class="pl-4" cols="2">
                <v-text-field
                  :label="`${$t('PluviometricChart.spans.length')} *`"
                  :title="`${$t('PluviometricChart.spans.length')} *`"
                  v-model="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.flowPerSpan.spans[spanIndex].length"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberRequired"
                  required
                  validate-on-blur
                  hide-details="auto"
                />
              </v-col>
            </v-row>
            <template v-for="(sprinkler, sprinklerIndex) in span.sprinklers">
              <v-row class="full-width" no-gutters>
                <p class="align-content-center mb-0">{{  getAbsoluteSprinklerNumber(spanIndex, sprinklerIndex) }}</p>
                <v-col class="mx-3">
                  <v-checkbox
                    v-model="sprinkler.isClogged"
                    :label="`${$t('PluviometricChart.sprinklers.isClogged')}`"
                  />
                </v-col>
                <template v-if="!sprinkler.isClogged">
                  <v-col>
                    <v-text-field
                      :label="`${$t('PluviometricChart.sprinklers.distToCenter')} *`"
                      :title="`${$t('PluviometricChart.sprinklers.distToCenter')} *`"
                      v-model.number="sprinkler.distanceToCenter"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      :rules="rules.numberRequired"
                      required
                      validate-on-blur
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col class="ml-2">
                    <v-select
                      :items="sprinklersBrands"
                      item-value="value"
                      item-text="name"
                      v-model="sprinkler.sprinklerBrand"
                      :label="`${$t('PluviometricChart.sprinklers.sprinklerBrand')} *`"
                      :title="`${$t('PluviometricChart.sprinklers.sprinklerBrand')} *`"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      :rules="rules.fieldRequired"
                      required
                      validate-on-blur
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col class="ml-2">
                    <v-select
                      :items="sprinklersModels(sprinkler.sprinklerBrand)"
                      item-value="value"
                      item-text="name"
                      v-model="sprinkler.sprinklerModel"
                      :label="`${$t('PluviometricChart.sprinklers.sprinklerModel')} *`"
                      :title="`${$t('PluviometricChart.sprinklers.sprinklerModel')} *`"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      :rules="rules.fieldRequired"
                      required
                      validate-on-blur
                      :disabled="!sprinkler.sprinklerBrand"
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col class="ml-2">
                    <v-select
                      :items="sprinklersSizes(sprinkler.sprinklerBrand, sprinkler.sprinklerModel)"
                      item-text="size"
                      item-value="size"
                      v-model="sprinkler.nozzleSize"
                      :label="`${$t('PluviometricChart.sprinklers.nozzleSize')} *`"
                      :title="`${$t('PluviometricChart.sprinklers.nozzleSize')} *`"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      :rules="rules.fieldRequired"
                      required
                      validate-on-blur
                      :disabled="!sprinkler.sprinklerModel"
                      hide-details="auto"
                      return-object
                    />
                  </v-col>
                  <v-col class="ml-2">
                    <v-text-field
                      :label="$t('PluviometricChart.sprinklers.wearPad')"
                      :title="$t('PluviometricChart.sprinklers.wearPad')"
                      v-model="sprinkler.wearPad"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col class="ml-2">
                    <v-text-field
                      :label="`${$t('PluviometricChart.sprinklers.dropHeight')} *`"
                      :title="`${$t('PluviometricChart.sprinklers.dropHeight')} *`"
                      v-model.number="sprinkler.dropHeight"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      :rules="rules.numberRequired"
                      required
                      validate-on-blur
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col class="ml-2">
                    <v-select
                      :items="[10, 15]"
                      v-model.number="sprinkler.regulator"
                      :label="`${$t('PluviometricChart.sprinklers.regulator')} *`"
                      :title="`${$t('PluviometricChart.sprinklers.regulator')} *`"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      :rules="rules.numberRequired"
                      required
                      validate-on-blur
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col class="ml-2">
                    <v-text-field
                      :label="`${$t('PluviometricChart.sprinklers.outletPressure')} *`"
                      :title="`${$t('PluviometricChart.sprinklers.outletPressure')} *`"
                      v-model.number="sprinkler.outletPressure"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      :rules="rules.numberRequired"
                      required
                      validate-on-blur
                      hide-details="auto"
                    />
                  </v-col>
                </template>
              </v-row>
            </template>
          </template>
          <p class="section-text text-left full-width">{{ $t('PluviometricChart.lrdu')}}<v-icon @click="showLRDUSection = !showLRDUSection" class="ml-1">{{showLRDUSection ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon></p>
          <template v-if="showLRDUSection">
            <v-row class="full-width" no-gutters>
              <v-col cols="3">
                <v-text-field
                  :label="$t('PluviometricChart.rpm')"
                  :title="$t('PluviometricChart.rpm')"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.lrduDriveTrain.rpm"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberOptional"
                />
              </v-col>
              <v-col cols="3" class="ml-3">
                <v-text-field
                  :label="$t('PluviometricChart.frequency')"
                  :title="$t('PluviometricChart.frequency')"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.lrduDriveTrain.frequency"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberOptional"
                />
              </v-col>
            </v-row>
            <v-row class="full-width" no-gutters>
              <v-col cols="3">
                <v-text-field
                  :label="$t('PluviometricChart.highFloat')"
                  :title="$t('PluviometricChart.highFloat')"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.lrduDriveTrain.highFloat"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberOptional"
                />
              </v-col>
            </v-row>
            <v-row class="full-width" no-gutters>
              <v-col cols="3">
                <v-text-field
                  :label="$t('PluviometricChart.wheelGBRatio')"
                  :title="$t('PluviometricChart.wheelGBRatio')"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.lrduDriveTrain.wheelGbRatio"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberOptional"
                />
              </v-col>
            </v-row>
            <v-row class="full-width" no-gutters>
              <v-col cols="3">
                <v-text-field
                  :label="$t('PluviometricChart.lrduDist')"
                  :title="$t('PluviometricChart.lrduDist')"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.lrduDriveTrain.lrduDist"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberOptional"
                />
              </v-col>
            </v-row>
            <v-row class="full-width" no-gutters>
              <v-col cols="3">
                <v-text-field
                  :label="$t('PluviometricChart.hoursPer360')"
                  :title="$t('PluviometricChart.hoursPer360')"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.lrduDriveTrain.hoursPer360"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberOptional"
                />
              </v-col>
              <v-col cols="3" class="ml-3">
                <v-text-field
                  :label="$t('PluviometricChart.distancePerTime')"
                  :title="$t('PluviometricChart.distancePerTime')"
                  v-model.number="pluviometricChartsData[selectedPluviometricConfiguration].advancedConfiguration.lrduDriveTrain.distancePerTime"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="rules.numberOptional"
                />
              </v-col>
            </v-row>
          </template>
        </v-row>
      </v-form>
    </v-col>
    <v-row v-if="!loadingCharts" class="mt-3 device-editor-button-row" align="end">
      <v-col cols="12" sm="4" offset-sm="4" class="align-center">
        <v-btn
          @click="saveHandler"
          block
          color="#0098E1"
          large
          class="form-confirmation-button"
          dark
          :loading="loading"
        >{{$t('AddDevice.register')}}</v-btn>
      </v-col>
    </v-row>
  </v-row>
</template>

<script>
// constants
import {
  ALLOWED_PLUVIOMETRIC_CHARTS_FILE_EXTENSIONS,
  ALLOWED_PLUVIOMETRIC_CHARTS_FILE_TYPES,
  EQUIPMENT_TYPE,
  EQUIPMENT_SUBTYPE,
  PRESSURE,
  DEFAULT_PLUVIOMETRIC_CHART_DATA,
  DEFAULT_FIELD_SOURCE,
  DEFAULT_PLUVIOMETRIC_CHART_ADVANCED_CONFIGURATION
} from '../../../constants/const'
import { getZoom, isoDateToSlashes, checkAndParseFloat, sortByName } from '../../../lib/helpers'
import { upsertDevice, createDraftDevice, uploadPluviometricChartFile, updateDevicePluviometricCharts } from '../../../lib/crudOperations/write'
import { callProcessPluviometricChart } from '../../../lib/pluviometricChartWebhook'
import _cloneDeep from 'lodash/cloneDeep'
import _get from 'lodash/get'
import _sumBy from 'lodash/sumBy'
import _sortBy from 'lodash/sortBy'
import { getParsedPluviometricChartsData, getPluviometricChartConstants, getDevice } from '../../../lib/crudOperations/read'

export default {
  data () {
    const equipmentTypes = [
      {label: this.$t('Config.pivot'), value: EQUIPMENT_TYPE.PIVOT},
      {label: this.$t('Config.forwardAvance'), value: EQUIPMENT_TYPE.AF},
      {label: this.$t('Config.irrigationReel'), value: EQUIPMENT_TYPE.IR}
    ]
    const equipmentSubtypes = {
      linear: [
        {label: this.$t('Config.linearTwoWings'), value: EQUIPMENT_SUBTYPE.AF.TWO_WINGS},
        {label: this.$t('Config.linearOneWing'), value: EQUIPMENT_SUBTYPE.AF.ONE_WING},
        {label: this.$t('Config.linearPivotable'), value: EQUIPMENT_SUBTYPE.AF.PIVOTABLE}
      ]
    }

    return {
      formValue: true,
      equipmentTypes,
      equipmentSubtypes,
      validityDateMenu: false,
      pluviometricChartDialog: false,
      allowedFileTypes: ALLOWED_PLUVIOMETRIC_CHARTS_FILE_EXTENSIONS,
      length: this.deviceLength,
      deviceData: this.deviceDataProp,
      oldDeviceData: {},
      pluviometricChartCreated: false,
      rules: {
        fieldRequired: [v => !!v || this.$t('Config.fieldIsRequired')],
        fileField: [this.checkFileType],
        numberRequired: [v => !!v || this.$t('Config.fieldIsRequired'), v => /^[+]?\d+([.]\d+)?$/.test(v) || this.$t('Config.onlyNumbers')],
        numberOptional: [v => !v || /^[+]?\d+([.]\d+)?$/.test(v) || this.$t('Config.onlyNumbers')],
        minPressure: [v => !!v || this.$t('Config.fieldIsRequired'), v => /^[+]?\d+([.]\d+)?$/.test(v) || this.$t('Config.onlyNumbers'), v => (!this.pluviometricChartsData[this.selectedPluviometricConfiguration].pressureRange.maxPressure || parseFloat(v) < parseFloat(this.pluviometricChartsData[this.selectedPluviometricConfiguration].pressureRange.maxPressure)) || this.$t('Config.minPressureRule')],
        maxPressure: [v => !!v || this.$t('Config.fieldIsRequired'), v => /^[+]?\d+([.]\d+)?$/.test(v) || this.$t('Config.onlyNumbers'), v => (!this.pluviometricChartsData[this.selectedPluviometricConfiguration].pressureRange.minPressure || parseFloat(v) > parseFloat(this.pluviometricChartsData[this.selectedPluviometricConfiguration].pressureRange.minPressure)) || this.$t('Config.maxPressureRule')],
        fieldRequiredIfPluviometricChart: [v => (!this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].pluviometricChartFile || (this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].pluviometricChartFile && !!v)) || this.$t('Config.fieldIsRequired')]
      },
      isNewDevice: this.$route.params.isNewDevice,
      loading: false,
      creationDateMenu: false,
      advancedConfigFlag: false,
      minIrrigationFile: require('@/assets/min-irrigation.png'),
      equipmentBrandsAndModels: {},
      tubeMaterials: [],
      sprinklersBrandsAndModels: [],
      showLRDUSection: false,
      showFlowSection: false,
      showFieldAreaSection: false,
      showPipesSection: false,
      EQUIPMENT_TYPE,
      selectedPluviometricConfiguration: undefined,
      pluviometricChartsData: [],
      oldPluviometricChartsData: [],
      loadingCharts: false,
      deletePluviometricChartDialog: false,
      deletePluviometricChartIndex: undefined,
      webhookLoadingByChartIndex: {},
      lastUploadedFileNameByChartIndex: {},
      isValidating: false
    }
  },
  async created () {
    this.loadingCharts = true
    try {
      const {brandsAndModels, tubeMaterials, sprinklersBrandsAndModels} = await getPluviometricChartConstants()
      this.equipmentBrandsAndModels = brandsAndModels
      this.tubeMaterials = tubeMaterials
      this.sprinklersBrandsAndModels = sprinklersBrandsAndModels

      if (!this.deviceData.pluviometricCharts.length) return
      const chartsData = await getParsedPluviometricChartsData(this.deviceData.pluviometricCharts)
      if (!chartsData.length) return
      this.pluviometricChartsData = chartsData
      this.selectedPluviometricConfiguration = this.deviceData.pluviometricCharts.length ? this.deviceData.pluviometricCharts.findIndex(chart => chart.isActive) !== -1 ? this.deviceData.pluviometricCharts.findIndex(chart => chart.isActive) : 0 : 0

      if (!this.pluviometricChartsData[this.selectedPluviometricConfiguration].advancedConfiguration) this.pluviometricChartsData[this.selectedPluviometricConfiguration].advancedConfiguration = DEFAULT_PLUVIOMETRIC_CHART_ADVANCED_CONFIGURATION
      this.addRequiredDeviceData()

      this.oldDeviceData = _cloneDeep(this.deviceData)
      this.oldPluviometricChartsData = _cloneDeep(this.pluviometricChartsData)

      // Update isActive status based on validity dates after loading
      this.updateActivePluviometricChart()
    } catch (error) {
      this.$root.snack.show({text: this.$t('UI.serverError'), error: true})
    } finally {
      this.loadingCharts = false
    }
  },
  beforeDestroy () {},
  computed: {
    brands () {
      const deviceType = this.deviceData.type
      if (deviceType && _get(this, `equipmentBrandsAndModels.${deviceType}.length`)) {
        const brandsData = []
        this.equipmentBrandsAndModels[deviceType].forEach(brand => {
          brandsData.push({
            name: brand.name,
            value: brand.name
          })
        })
        return brandsData.sort(sortByName).concat([{name: this.$t('PluviometricChart.otherBrand'), value: 'other'}])
      }
      return [{name: this.$t('PluviometricChart.otherBrand'), value: 'other'}]
    },
    currentBrand () {
      if (this.selectedPluviometricConfiguration !== undefined &&
          this.pluviometricChartsData &&
          this.pluviometricChartsData[this.selectedPluviometricConfiguration]) {
        const brand = this.pluviometricChartsData[this.selectedPluviometricConfiguration].brand
        return brand || null
      }
      return null
    },
    currentChartTubeQuantity () {
      if (this.selectedPluviometricConfiguration === undefined) return null
      const chart = this.pluviometricChartsData[this.selectedPluviometricConfiguration]
      return chart && chart.advancedConfiguration && chart.advancedConfiguration.pipes
        ? chart.advancedConfiguration.pipes.tubeQuantity
        : null
    },
    currentChartSpanQuantity () {
      if (this.selectedPluviometricConfiguration === undefined) return null
      const chart = this.pluviometricChartsData[this.selectedPluviometricConfiguration]
      return chart && chart.advancedConfiguration && chart.advancedConfiguration.flowPerSpan
        ? chart.advancedConfiguration.flowPerSpan.spanQuantity
        : null
    },
    currentChartSpans () {
      if (this.selectedPluviometricConfiguration === undefined) return []
      const chart = this.pluviometricChartsData[this.selectedPluviometricConfiguration]
      return chart && chart.advancedConfiguration && chart.advancedConfiguration.flowPerSpan
        ? chart.advancedConfiguration.flowPerSpan.spans
        : []
    },
    models () {
      const deviceType = this.deviceData.type
      const currentBrand = this.currentBrand
      if (currentBrand && currentBrand !== 'other' && deviceType && _get(this, `equipmentBrandsAndModels.${deviceType}.length`)) {
        let brandData = this.equipmentBrandsAndModels[deviceType].find(elem => elem.name === currentBrand)
        if (brandData && brandData.models) {
          const modelsData = []
          brandData.models.forEach(model => {
            modelsData.push({
              name: model,
              value: model
            })
          })
          return modelsData.sort(sortByName).concat([{name: this.$t('PluviometricChart.otherModel'), value: 'other'}])
        }
      }
      return [{name: this.$t('PluviometricChart.otherModel'), value: 'other'}]
    },
    sprinklersBrands () {
      return this.sprinklersBrandsAndModels
        .map(brand => ({
          name: brand.name,
          value: brand.name
        }))
        .sort(sortByName)
    },
    tubeMaterialsItems () {
      if (this.tubeMaterials) {
        const materialsData = []
        this.tubeMaterials.forEach(material => {
          materialsData.push({
            name: this.$t(`PluviometricChart.tubeMaterials.${material}`),
            value: material
          })
        })
        return materialsData.sort(sortByName).concat([{name: this.$t('PluviometricChart.tubeMaterials.other'), value: 'other'}])
      }
      return [{name: this.$t('PluviometricChart.tubeMaterials.other'), value: 'other'}]
    },
    isIREquipment () {
      return this.deviceData.type === EQUIPMENT_TYPE.IR
    },
    isPivotEquipment () {
      return this.deviceData.type === EQUIPMENT_TYPE.PIVOT
    },
    isAFEquipment () {
      return this.deviceData.type === EQUIPMENT_TYPE.AF
    },
    deviceHaveCrops () {
      const device = this.$store.getters.devices.find(dev => dev.id === this.deviceData.id)
      const cropsLength = _get(device, 'campaign.crops', []).length
      return !this.isNewDevice ? cropsLength : false
    },
    canImpersonate () {
      const user = this.$store.getters.user
      return this.$store.getters.impersonatedUserId || (user && user.isAdmin)
    },
    fieldsLockedByWebhook () {
      if (this.selectedPluviometricConfiguration === undefined || this.selectedPluviometricConfiguration === null) return false
      return !!this.webhookLoadingByChartIndex[this.selectedPluviometricConfiguration]
    },
    hasAutomaticReadingForCurrentChart () {
      if (this.selectedPluviometricConfiguration === undefined || this.selectedPluviometricConfiguration === null) return false
      const chart = this.pluviometricChartsData[this.selectedPluviometricConfiguration]
      if (!chart || !chart.fieldSource) return false
      return Object.values(chart.fieldSource).some(v => v === 'auto')
    }
  },
  props: {
    deviceDataProp: {
      type: Object,
      required: true
    },
    deviceLength: {
      type: Number,
      required: true
    },
    pluviometricChartData: {
      type: Object,
      required: true
    }
  },
  methods: {
    parseDeviceID () {
      this.deviceData.id = _get(this.deviceData, 'id', '').replace(/ /g, '') // remove all the whitespaces
    },
    triggerFileSelector () {
      this.$refs.FileSelector.$refs.input.click()
    },
    setFieldSourceManual (fieldKey) {
      if (this.selectedPluviometricConfiguration === undefined || this.selectedPluviometricConfiguration === null) return
      const chart = this.pluviometricChartsData[this.selectedPluviometricConfiguration]
      if (chart) {
        if (!chart.fieldSource) this.$set(chart, 'fieldSource', { ...DEFAULT_FIELD_SOURCE })
        this.$set(chart.fieldSource, fieldKey, 'manual')
      }
    },
    /** True when this field was loaded by webhook (DEFAULT_FIELD_SOURCE keys only) */
    isFieldFromWebhook (fieldKey) {
      if (this.selectedPluviometricConfiguration === undefined || this.selectedPluviometricConfiguration === null) return false
      if (!Object.keys(DEFAULT_FIELD_SOURCE).includes(fieldKey)) return false
      const chart = this.pluviometricChartsData[this.selectedPluviometricConfiguration]
      return chart && chart.fieldSource && chart.fieldSource[fieldKey] === 'auto'
    },
    /**
     * Normalizes payload from webhook or Firestore so the form can consume it.
     * - Uses inner .data when present (backend may store as { data: { ... } })
     * - Maps advancedConfiguration.pressure.basePress -> pressureRange.chartPressure
     * - Ensures document_date is available for creation date if needed
     */
    normalizeChartPayload (raw) {
      if (!raw) return null
      const data = raw.data != null ? raw.data : raw
      if (!data || typeof data !== 'object') return data
      const out = { ...data }
      const basePress = data.advancedConfiguration && data.advancedConfiguration.pressure && data.advancedConfiguration.pressure.basePress
      if (basePress && (out.pressureRange == null || (out.pressureRange.chartPressure == null && out.pressureRange.chartPressure !== 0))) {
        out.pressureRange = { ...(out.pressureRange || {}), chartPressure: basePress.value, unit: basePress.unit || (out.pressureRange && out.pressureRange.unit) || 'bar' }
      }
      return out
    },
    applyChartDataFromSnapshot (index, data, chartId) {
      const payload = this.normalizeChartPayload(data)
      if (!this.pluviometricChartsData[index] || !payload) return
      const chart = this.pluviometricChartsData[index]
      if (!chart.fieldSource) this.$set(chart, 'fieldSource', { ...DEFAULT_FIELD_SOURCE })
      if (!chart.pressureRange || typeof chart.pressureRange !== 'object') this.$set(chart, 'pressureRange', { unit: 'bar' })

      // Manual input always wins: do not overwrite fields the user already edited
      const isFirstApplyFromWebhook = Object.keys(DEFAULT_FIELD_SOURCE).every(k => chart.fieldSource[k] === 'manual')
      const fieldsToMerge = [
        { key: 'pumpFlow', sourceKey: 'pumpFlow' },
        { key: 'brand', sourceKey: 'brand' },
        { key: 'model', sourceKey: 'model' },
        { key: 'deviceLength', sourceKey: 'deviceLength' },
        { key: 'area', sourceKey: 'area' },
        { key: 'equipmentReach', sourceKey: 'equipmentReach' },
        { key: 'hoseLength', sourceKey: 'hoseLength' },
        { key: 'pressureRange', sourceKey: 'pressureRange' }
      ]
      fieldsToMerge.forEach(({ key, sourceKey }) => {
        if (payload[sourceKey] === undefined || payload[sourceKey] === null) return

        if (key === 'pressureRange') {
          const src = payload.pressureRange || {}
          const pressureSourceKeys = [
            { fieldSourceKey: 'chartPressure', dataKey: 'chartPressure' },
            { fieldSourceKey: 'minPressure', dataKey: 'minPressure' },
            { fieldSourceKey: 'maxPressure', dataKey: 'maxPressure' }
          ]
          const merged = { ...(chart.pressureRange || {}), ...src }
          pressureSourceKeys.forEach(({ fieldSourceKey, dataKey }) => {
            if (!isFirstApplyFromWebhook && chart.fieldSource[fieldSourceKey] === 'manual' && (chart.pressureRange && chart.pressureRange[dataKey] !== undefined)) {
              merged[dataKey] = chart.pressureRange[dataKey]
            } else if (src[dataKey] !== undefined && src[dataKey] !== null) {
              this.$set(chart.fieldSource, fieldSourceKey, 'auto')
            }
          })

          // Webhook only provides chartPressure; calculate min/max from it when not manually set
          if (src.chartPressure !== undefined && src.chartPressure !== null) {
            const pressure = parseFloat(src.chartPressure)
            if (!isNaN(pressure)) {
              const canCalcMin = isFirstApplyFromWebhook || !(chart.fieldSource['minPressure'] === 'manual' && chart.pressureRange && chart.pressureRange.minPressure !== undefined)
              const canCalcMax = isFirstApplyFromWebhook || !(chart.fieldSource['maxPressure'] === 'manual' && chart.pressureRange && chart.pressureRange.maxPressure !== undefined)
              const { minPressure, maxPressure } = this.calcMinMaxPressure(pressure)
              if (canCalcMin) {
                merged.minPressure = minPressure
                this.$set(chart.fieldSource, 'minPressure', 'manual')
              }
              if (canCalcMax) {
                merged.maxPressure = maxPressure
                this.$set(chart.fieldSource, 'maxPressure', 'manual')
              }
            }
          }

          this.$set(chart, 'pressureRange', merged)
          return
        }

        if (!isFirstApplyFromWebhook && chart.fieldSource[key] === 'manual') return
        const val = payload[sourceKey]
        if (typeof val === 'object' && !Array.isArray(val) && val !== null) {
          this.$set(chart, key, { ...(chart[key] || {}), ...val })
        } else {
          this.$set(chart, key, val)
        }
        // Only mark as AI-loaded when the value is non-empty (avoids AI icon on blank fields)
        const isMeaningful = typeof val === 'string'
          ? val.trim() !== ''
          : typeof val === 'object' && val !== null
            ? 'value' in val && val.value !== null && val.value !== undefined && val.value !== ''
            : true
        if (isMeaningful) {
          this.$set(chart.fieldSource, key, 'auto')
        }
      })

      if (chartId && this.deviceData.pluviometricCharts[index]) {
        this.$set(this.deviceData.pluviometricCharts[index], 'parsedChartId', chartId)
        this.$set(this.deviceData.pluviometricCharts[index], 'read', true)
        const chart = this.pluviometricChartsData[index]
        const deviceChart = this.deviceData.pluviometricCharts[index]
        if (chart && chart.fieldSource) {
          this.$set(this.deviceData.pluviometricCharts[index], 'fieldSource', { ...chart.fieldSource })
        }
        // Persist read and fieldSource on the device so they survive refresh
        if (this.deviceData.id) {
          const payload = { read: true }
          if (deviceChart.linkToFile) payload.linkToFile = deviceChart.linkToFile
          if (chart && chart.fieldSource) payload.fieldSource = chart.fieldSource
          updateDevicePluviometricCharts(this.deviceData.id, index, payload).catch(err =>
            console.error('Failed to persist pluviometric chart read state:', err)
          )
        }
      }
    },
    async processChartFile (index) {
      const chartMeta = this.deviceData.pluviometricCharts[index]
      if (!chartMeta) return
      const file = chartMeta.pluviometricChartFile
      if (!file || !(file instanceof File) || file.size === 0) return
      if (chartMeta.fileAlreadyUploaded) return
      // Prevent duplicate webhook calls: set loading first so the deep watcher skips re-entry
      if (this.webhookLoadingByChartIndex[index]) return
      this.$set(this.webhookLoadingByChartIndex, index, true)

      // New PDF loaded (different from previous): mark chart as unread until webhook completes
      this.$set(chartMeta, 'read', false)

      try {
        let deviceId = this.deviceData.id
        if (this.isNewDevice && !deviceId) {
          deviceId = await createDraftDevice(this.deviceData)
          this.$set(this.deviceData, 'id', deviceId)
        }
        if (!deviceId) {
          this.$set(this.webhookLoadingByChartIndex, index, false)
          this.$root.snack.show({ text: this.$t('UI.serverError'), error: true })
          return
        }

        const storagePath = await uploadPluviometricChartFile(file, this.deviceData.name || 'device')
        this.$set(chartMeta, 'linkToFile', storagePath)
        this.$set(chartMeta, 'fileAlreadyUploaded', true)
        this.$set(this.lastUploadedFileNameByChartIndex, index, file.name)

        // Link chart to device in Firestore before webhook so backend sees "chart is linked to this device"
        const chartPayload = {
          linkToFile: storagePath,
          read: false,
          validityDate: chartMeta.validityDate ? new Date(chartMeta.validityDate) : '',
          creationDate: chartMeta.creationDate ? new Date(chartMeta.creationDate) : '',
          saveDate: new Date(),
          isActive: chartMeta.isActive || false
        }
        await updateDevicePluviometricCharts(deviceId, index, chartPayload)

        const result = await callProcessPluviometricChart(deviceId, storagePath)

        if (!result.ok) {
          this.$set(this.webhookLoadingByChartIndex, index, false)
          this.$root.snack.show({ text: this.$t('UI.serverError'), error: true })
          return
        }

        const chartId = result.chartId || (result.data && result.data.chartId)
        const responseData = result.data && (result.data.data || result.data)
        if (chartId) {
          if (responseData && Object.keys(responseData).length) {
            this.applyChartDataFromSnapshot(index, responseData, chartId)
          } else {
            this.$root.snack.show({ text: this.$t('UI.serverError'), error: true })
          }
          this.$set(this.webhookLoadingByChartIndex, index, false)
        } else {
          this.$set(this.webhookLoadingByChartIndex, index, false)
        }
      } catch (err) {
        console.error('processChartFile error:', err)
        this.$set(this.webhookLoadingByChartIndex, index, false)
        this.$root.snack.show({ text: this.$t('UI.serverError'), error: true })
      }
    },
    async validateAllPluviometricCharts () {
      this.isValidating = true
      // Save current selection to restore it later
      const originalIndex = this.selectedPluviometricConfiguration
      let invalidIndex = -1
      let minValidityDate
      let minValidityDateIndex = -1
      let pluviometricChartFromStartServiceDate = false

      // Validate all pluviometric charts
      for (let index = 0; index < this.deviceData.pluviometricCharts.length; index++) {
        this.selectedPluviometricConfiguration = index
        // Use $nextTick instead of setTimeout for better performance and faster validation
        await this.$nextTick()
        // Small delay to ensure form is rendered (reduced from 100ms)
        await new Promise(resolve => setTimeout(resolve, 10))

        const isDeviceFormValid = this.$refs.deviceForm && this.$refs.deviceForm.validate()
        const isAdvancedFormValid = !this.$refs.advancedDeviceForm || this.deviceData.type === EQUIPMENT_TYPE.IR ||this.$refs.advancedDeviceForm.validate()

        if (!isDeviceFormValid || !isAdvancedFormValid) {
          invalidIndex = index
          break
        }

        if (!minValidityDate || new Date(this.deviceData.pluviometricCharts[index].validityDate) < minValidityDate) {
          minValidityDate = new Date(this.deviceData.pluviometricCharts[index].validityDate)
          minValidityDateIndex = index
        }

        if (!pluviometricChartFromStartServiceDate && new Date(this.deviceData.pluviometricCharts[index].validityDate) <= this.deviceData.serviceStartDate) {
          pluviometricChartFromStartServiceDate = true
        }
      }

      // Always restore to where the user was — never navigate away on validation failure
      this.selectedPluviometricConfiguration = originalIndex
      await this.$nextTick()

      this.isValidating = false

      if (invalidIndex !== -1) {
        // Re-validate current chart to show its own errors if any
        this.$refs.deviceForm && this.$refs.deviceForm.validate()
        this.$refs.advancedDeviceForm && this.$refs.advancedDeviceForm.validate()
        this.$root.snack.show({text: this.$t('Config.fillRequiredFields'), error: true})
        return false
      }

      if (!pluviometricChartFromStartServiceDate) {
        this.selectedPluviometricConfiguration = minValidityDateIndex !== -1 ? minValidityDateIndex : originalIndex
        await this.$nextTick()
        this.$root.snack.show({text: this.$t('AddDevice.pluviometricChartFromStartServiceDateError'), error: true})
        return false
      }
      
      return true
    },
    async saveHandler () {
      // Validate all pluviometric charts before saving
      const isValid = await this.validateAllPluviometricCharts()
      if (!isValid) {
        return
      }
      
      const chartsDataWithValidityDate = this.pluviometricChartsData.map((chart, index) => {
        return {
          ...chart,
          validityDate: this.deviceData.pluviometricCharts[index].validityDate
        }
      })
      const sortedCharts = chartsDataWithValidityDate.sort((a, b) => new Date(b.validityDate) - new Date(a.validityDate))
      // Find the first chart that is valid for the block
      let activeChartIndex = sortedCharts.findIndex(chart => {
        return new Date() >= new Date(chart.validityDate)
      })

      if (activeChartIndex === -1 && this.deviceData.serviceStartDate <= new Date()) {
        this.$root.snack.show({text: this.$t('AddDevice.noActivePluviometricChart'), error: true})
        return
      }

      const activeChart = sortedCharts[activeChartIndex !== -1 ? activeChartIndex : 0]

      this.deviceData.zoom = getZoom(this.deviceData.type, activeChart.deviceLength.value)
      this.loading = true
      try {
        this.addRequiredDeviceData(activeChart)

        // If the device is new and the ID was set manually, first check if it exists
        const deviceID = _get(this.deviceData, 'id', '')
        if (deviceID && this.isNewDevice) {
          const device = await getDevice(deviceID)
          if (device) return this.$root.snack.show({text: this.$t('UI.existingDeviceError'), error: true})
        }

        await upsertDevice(
          this.oldDeviceData,
          {...this.deviceData, hasCompleteData: true},
          this.oldPluviometricChartsData,
          this.pluviometricChartsData,
          this.isNewDevice
        )
        this.$root.snack.show({text: this.$t('UI.registerSuccess')})
        this.$router.push({name: 'OrganizationHome'})
      } catch (error) {
        this.$root.snack.show({text: this.$t('UI.registerError'), error: true})
      } finally {
        this.loading = false
      }
    },
    checkFileType (file) {
      if (!file || !file.type) return true

      return ALLOWED_PLUVIOMETRIC_CHARTS_FILE_TYPES.includes(file.type)
        ? true
        : this.$t(
          'AddDevice.allowedFileTypes',
          [ALLOWED_PLUVIOMETRIC_CHARTS_FILE_EXTENSIONS]
        )
    },
    addRequiredDeviceData (activeChart) {
      let activeChartData = activeChart
      if (!activeChart) {
        activeChartData = this.pluviometricChartsData[this.deviceData.pluviometricCharts.findIndex(chart => chart.isActive)]
      }
      this.deviceData = {
        ...this.deviceData,
        equipment: {
          ...this.deviceData.equipment,
          brand: _get(activeChartData, 'brand', ''),
          model: _get(activeChartData, 'model', ''),
          type: this.deviceData.type,
          subtype: this.deviceData.subtype
        },
        pump: {
          flow: checkAndParseFloat(activeChartData, 'pumpFlow.value'),
          pressure: checkAndParseFloat(activeChartData, 'pressureRange.chartPressure'),
          threshold: {
            low: checkAndParseFloat(activeChartData, 'pressureRange.minPressure'),
            high: checkAndParseFloat(activeChartData, 'pressureRange.maxPressure')
          }
        }
      }
      if (this.isIREquipment) {
        this.deviceData.equipment.reach = checkAndParseFloat(activeChartData, 'equipmentReach.value')
        this.deviceData.equipment.hoseLength = checkAndParseFloat(activeChartData, 'hoseLength.value')
      } else {
        this.deviceData.equipment.area = checkAndParseFloat(activeChartData, 'area.value')
        this.deviceData.equipment.length = checkAndParseFloat(activeChartData, 'deviceLength.value')
      }
    },
    clearValidityDate () {
      this.pluviometricChartsData[this.selectedPluviometricConfiguration].auxValidityDate = ''
    },
    clearCreationDate () {
      this.auxCreationDate = ''
    },
    calcMinMaxPressure (pressure) {
      const min = (pressure - PRESSURE.MIN_RANGE).toFixed(2)
      return {
        minPressure: parseFloat(min) < 0 ? PRESSURE.MIN : min,
        maxPressure: (pressure + PRESSURE.MAX_RANGE).toFixed(2)
      }
    },
    onChartPressureInput (_pressure) {
      const pressure = parseFloat(_pressure)
      const { minPressure, maxPressure } = this.calcMinMaxPressure(pressure)

      this.pluviometricChartsData[this.selectedPluviometricConfiguration].pressureRange.minPressure = minPressure
      this.pluviometricChartsData[this.selectedPluviometricConfiguration].pressureRange.maxPressure = maxPressure
      this.setFieldSourceManual('chartPressure')
      this.setFieldSourceManual('minPressure')
      this.setFieldSourceManual('maxPressure')
    },
    sprinklersModels (brand) {
      if (!brand) return []

      let brandData = this.sprinklersBrandsAndModels.find(elem => elem.name === brand)
      if (!brandData || !brandData.models) return []

      const modelsData = []
      brandData.models.forEach(model => {
        modelsData.push({
          name: model.name,
          value: model.name
        })
      })
      return modelsData.sort(sortByName)
    },
    sprinklersSizes (brand, model) {
      if (!brand || !model) return []

      let brandData = this.sprinklersBrandsAndModels.find(elem => elem.name === brand)
      if (!brandData || !brandData.models) return []

      let modelData = brandData.models.find(elem => elem.name === model)
      if (!modelData || !modelData.nozzleSizes) return []

      return _sortBy([...modelData.nozzleSizes], ['size'])
    },
    getAbsoluteSprinklerNumber(spanIndex, sprinklerIndex) {
      const previousSprinklers = _sumBy(
        this.pluviometricChartsData[this.selectedPluviometricConfiguration].advancedConfiguration.sprinklerConfiguration.sprinklersPerSpan.slice(0, spanIndex),
        span => span.sprinklers.length
      )

      return previousSprinklers + sprinklerIndex + 1
    },
    parseValidityDate (validityDate) {
      return validityDate ? isoDateToSlashes(validityDate) : ''
    },
    updateValidityDate () {
      this.validityDateMenu = false
      if (!this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].auxValidityDate) {
        this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].formattedValidityDate = ''
        this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].validityDate = ''
      } else {
        this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].validityDate = new Date(this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].auxValidityDate).toISOString()
        this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].formattedValidityDate = isoDateToSlashes(this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].validityDate)
      }
      // Update isActive status based on validity dates
      this.updateActivePluviometricChart()
    },
    updateActivePluviometricChart () {
      // Filter charts with valid validityDate and create array with index
      const chartsWithDates = this.deviceData.pluviometricCharts
        .map((chart, index) => ({
          index,
          validityDate: chart.validityDate,
          chart
        }))
        .filter(item => item.validityDate) // Only charts with validityDate

      if (chartsWithDates.length === 0) {
        // If no charts have validityDate, set all to inactive
        this.deviceData.pluviometricCharts.forEach(chart => {
          this.$set(chart, 'isActive', false)
        })
        return
      }

      // Sort descending by validityDate (most recent first)
      chartsWithDates.sort((a, b) => new Date(b.validityDate) - new Date(a.validityDate))

      // Find the first chart whose validityDate is before or equal to current date
      const now = new Date()
      const activeChartIndex = chartsWithDates.findIndex(item => {
        return new Date(item.validityDate) <= now
      })

      // Set all charts to inactive first
      this.deviceData.pluviometricCharts.forEach(chart => {
        this.$set(chart, 'isActive', false)
      })

      // Set the active chart if found
      if (activeChartIndex !== -1) {
        const activeIndex = chartsWithDates[activeChartIndex].index
        this.$set(this.deviceData.pluviometricCharts[activeIndex], 'isActive', true)
      }
    },
    updateCreationDate () {
      this.creationDateMenu = false
      if (!this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].auxCreationDate) {
        this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].formattedCreationDate = ''
        this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].creationDate = ''
      } else {
        this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].creationDate = new Date(this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].auxCreationDate).toISOString()
        this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].formattedCreationDate = isoDateToSlashes(this.deviceData.pluviometricCharts[this.selectedPluviometricConfiguration].creationDate)
      }
    },
    updateDeviceLength () {
      if (this.pluviometricChartsData[this.selectedPluviometricConfiguration].deviceLength.value) {
        this.length = parseFloat(this.pluviometricChartsData[this.selectedPluviometricConfiguration].deviceLength.value)
      }
    },
    updateBrand () {
      // Use $set to ensure Vue reactivity when updating nested properties
      const currentChart = this.pluviometricChartsData[this.selectedPluviometricConfiguration]
      if (currentChart.brand === 'other') {
        this.$set(currentChart, 'model', 'other')
      } else {
        // Reset model when brand changes to allow user to select a new model
        this.$set(currentChart, 'model', null)
      }
    },
    onBrandChange () {
      this.updateBrand()
      this.setFieldSourceManual('brand')
    },
    addPluviometricChart () {
      this.deviceData.pluviometricCharts.push({
        isActive: false,
        pluviometricChartFile: null,
        formattedValidityDate: '',
        formattedCreationDate: ''
      })
      this.pluviometricChartsData.push(_cloneDeep(DEFAULT_PLUVIOMETRIC_CHART_DATA))
      this.selectedPluviometricConfiguration = this.deviceData.pluviometricCharts.length - 1
    },
    handleDeletePluviometricChart (index) {
      this.deletePluviometricChartIndex = index
      this.deletePluviometricChartDialog = true
    },
    cancelDeletePluviometricChart () {
      this.deletePluviometricChartDialog = false
      this.deletePluviometricChartIndex = undefined
    },
    deletePluviometricChart () {
      const index = this.deletePluviometricChartIndex
      if (this.selectedPluviometricConfiguration === index) {
        if (this.deviceData.pluviometricCharts.length === 1) {
          this.selectedPluviometricConfiguration = undefined
        } else if (index === this.deviceData.pluviometricCharts.length - 1) {
          this.selectedPluviometricConfiguration = index - 1
        }
      } else if (index < this.selectedPluviometricConfiguration) {
        this.selectedPluviometricConfiguration = this.selectedPluviometricConfiguration - 1
      }
      this.deviceData.pluviometricCharts.splice(index, 1)
      this.pluviometricChartsData.splice(index, 1)
      // Update isActive status after deleting a chart
      this.updateActivePluviometricChart()
      this.deletePluviometricChartDialog = false
      this.deletePluviometricChartIndex = undefined
    },
    isChartDataEmpty (chartData) {
      return (this.deviceData.type === EQUIPMENT_TYPE.IR && !chartData.pumpFlow.value && !chartData.validityDate && !chartData.brand && !chartData.model && !chartData.hoseLength.value && !chartData.cannonReach.value) || (this.deviceData.type !== EQUIPMENT_TYPE.IR && !chartData.pumpFlow.value && !chartData.validityDate && !chartData.brand && !chartData.model && !chartData.deviceLength.value && !chartData.area.value)
    }
  },
  watch: {
    deviceData: {
      handler (val) {
        this.$emit('update:deviceData', val)
      },
      deep: true
    },
    selectedPluviometricConfiguration: {
      handler (newVal, oldVal) {
        if (newVal === undefined) {
          setTimeout(() => {
            this.selectedPluviometricConfiguration = oldVal
          }, 10)
        }
        if (this.advancedConfigFlag) this.advancedConfigFlag = false
        if (newVal !== undefined && newVal !== null) {
          const chart = this.deviceData.pluviometricCharts[newVal]
          const file = chart && chart.pluviometricChartFile
          if (file && file instanceof File && file.size > 0 && !chart.fileAlreadyUploaded) {
            this.processChartFile(newVal)
          }
        }
        if (newVal !== oldVal && newVal !== undefined) {
          this.$nextTick(() => {
            this.currentChartTubeQuantity
            this.currentChartSpanQuantity
            this.currentChartSpans
            if (!this.isValidating) {
              this.$refs.deviceForm && this.$refs.deviceForm.resetValidation()
              this.$refs.advancedDeviceForm && this.$refs.advancedDeviceForm.resetValidation()
            }
          })
        }
      }
    },
    'deviceData.pluviometricCharts': {
      handler (charts) {
        if (!charts || this.selectedPluviometricConfiguration === undefined) return
        const index = this.selectedPluviometricConfiguration
        const chart = charts[index]
        if (!chart) return
        // Skip when we're already processing this chart (avoids duplicate webhook calls from deep watch)
        if (this.webhookLoadingByChartIndex[index]) return
        const file = chart.pluviometricChartFile
        if (!file || !(file instanceof File) || file.size === 0) {
          if (chart.fileAlreadyUploaded || chart.linkToFile) {
            this.$set(chart, 'fileAlreadyUploaded', false)
            this.$set(chart, 'linkToFile', undefined)
            this.$set(chart, 'read', false)
          }
          return
        }
        // Only clear and allow re-upload when user selected a *different* file (different name than last uploaded)
        // Avoids double webhook call when watcher runs after same upload (we have fileAlreadyUploaded + linkToFile)
        const lastUploaded = this.lastUploadedFileNameByChartIndex[index]
        const isDifferentFile = chart.linkToFile && file.name !== lastUploaded
        if (isDifferentFile) {
          this.$set(chart, 'read', false)
          this.$set(chart, 'fileAlreadyUploaded', false)
        }
        if (!chart.fileAlreadyUploaded) {
          this.processChartFile(index)
        }
      },
      deep: true
    },
    // Watch tubeQuantity for the currently selected pluviometric chart
    // Observes both the computed property and selectedPluviometricConfiguration
    currentChartTubeQuantity () {
      if (this.selectedPluviometricConfiguration === undefined) return
      const chart = this.pluviometricChartsData[this.selectedPluviometricConfiguration]
      if (!chart || !chart.advancedConfiguration || !chart.advancedConfiguration.pipes) return
      
      const val = parseInt(chart.advancedConfiguration.pipes.tubeQuantity)
      if (val && !isNaN(val)) {
        const tubesLength = chart.advancedConfiguration.pipes.tubes.length
        if (tubesLength < val) {
          for (let i = 0; i < val - tubesLength; i++) {
            chart.advancedConfiguration.pipes.tubes.push({})
          }
        } else if (tubesLength > val) {
          chart.advancedConfiguration.pipes.tubes.splice((tubesLength - (tubesLength - val)), (tubesLength - val))
        }
      }
    },
    // Watch spanQuantity for the currently selected pluviometric chart
    // Observes both the computed property and selectedPluviometricConfiguration
    currentChartSpanQuantity () {
      if (this.selectedPluviometricConfiguration === undefined) return
      const chart = this.pluviometricChartsData[this.selectedPluviometricConfiguration]
      const flowPerSpan = chart && chart.advancedConfiguration && chart.advancedConfiguration.flowPerSpan
      if (!chart || !flowPerSpan || !Array.isArray(flowPerSpan.spans)) return
      const sprinklerConfig = chart.advancedConfiguration.sprinklerConfiguration
      if (!sprinklerConfig || !Array.isArray(sprinklerConfig.sprinklersPerSpan)) return

      const val = parseInt(flowPerSpan.spanQuantity)
      if (val !== undefined && !isNaN(val)) {
        const spansLength = flowPerSpan.spans.length
        if (spansLength < val) {
          for (let i = 0; i < val - spansLength; i++) {
            flowPerSpan.spans.push({})
            sprinklerConfig.sprinklersPerSpan.push({ sprinklers: [] })
          }
        } else if (spansLength > val) {
          flowPerSpan.spans.splice((spansLength - (spansLength - val)), (spansLength - val))
          sprinklerConfig.sprinklersPerSpan.splice((spansLength - (spansLength - val)), (spansLength - val))
        }
      }
    },
    // Watch spans for the currently selected pluviometric chart
    currentChartSpans: {
      handler () {
        if (this.selectedPluviometricConfiguration === undefined) return
        const chart = this.pluviometricChartsData[this.selectedPluviometricConfiguration]
        if (!chart || !chart.advancedConfiguration || !chart.advancedConfiguration.flowPerSpan) return
        const spans = chart.advancedConfiguration.flowPerSpan.spans
        if (!Array.isArray(spans)) return
        const sprinklersPerSpan = chart.advancedConfiguration.sprinklerConfiguration && chart.advancedConfiguration.sprinklerConfiguration.sprinklersPerSpan
        if (!Array.isArray(sprinklersPerSpan)) return
        spans.forEach((span, index) => {
          const spanSprinklers = sprinklersPerSpan[index]
          if (!spanSprinklers || !Array.isArray(spanSprinklers.sprinklers)) return
          const sprinklerQuantity = parseInt(span.sprinklerQuantity)
          if (sprinklerQuantity && !isNaN(sprinklerQuantity)) {
            const sprinklersLength = spanSprinklers.sprinklers.length
            if (sprinklersLength < sprinklerQuantity) {
              for (let i = 0; i < sprinklerQuantity - sprinklersLength; i++) {
                spanSprinklers.sprinklers.push({})
              }
            } else if (sprinklersLength > sprinklerQuantity) {
              spanSprinklers.sprinklers.splice((sprinklersLength - (sprinklersLength - sprinklerQuantity)), (sprinklersLength - sprinklerQuantity))
            }
          }
        })
      },
      deep: true
    },
    deletePluviometricChartDialog (val) {
      if (!val) {
        this.deletePluviometricChartIndex = undefined
      }
    }
  }
}
</script>

<style scoped>
/* Middle col: fixed height so it never stretches down to Marca; content centered inside */
.automatic-reading-col {
  height: 3.5rem;
  max-height: 3.5rem;
  display: flex;
  flex-direction: column;
  align-self: center;
  margin-top: -1.5625rem; 
}
.automatic-reading-col-inner {
  height: 100%;
  display: flex;
  align-items: center;
}
.automatic-reading-block {
  display: inline-flex;
  align-items: center;
  color: #FFAD46;
}
.automatic-reading-label {
  color: #FFAD46;
}
</style>



// WEBPACK FOOTER //
// DeviceEditor.vue?00022c2e